import { Suspense } from 'react';

import { Link, Outlet } from '@tanstack/react-router';
import { Spinner } from 'cpool-components/state/Spinner';
import { useTranslation } from 'react-i18next';

import { getVisibleManagementTabs } from '~/components/Header/navigation/subRoutes/getVisibleManagementTabs';
import useUserInfo from '~/hooks/useUserInfo';
import { cn } from '~/utils/cnTailwind';

/**
 * The tabs mirror the Button component of the design system, but have to stay anchors so the router
 * keeps handling them - a <button> nested in a <Link> would be invalid markup - KC-6459
 */
const buttonClasses = {
    activeClasses: 'bg-midnight text-white',
    baseClasses: 'flex-1 rounded px-2.5 py-1.5 text-sm text-center whitespace-nowrap transition-colors select-none',
    inActiveClasses: 'bg-white text-midnight hover:bg-grey-table-hover',
};

export const Management = () => {
    const { t } = useTranslation();
    const user = useUserInfo();
    if (!user) {
        return null;
    }

    const tabs = getVisibleManagementTabs(user.role);

    return (
        <section data-testid="management-section" className="flex flex-1 flex-col">
            {tabs.length > 1 && (
                <div className="sticky top-0 z-40 px-3 py-4 border-b-2 border-white bg-surface-light sm:px-4 xl:px-8">
                    <div className="flex flex-wrap items-center gap-0.5">
                        {tabs.map((tab) => (
                            <Link
                                key={tab.to}
                                to={tab.to}
                                search={tab.search}
                                draggable={false}
                                inactiveProps={{
                                    className: cn(buttonClasses.baseClasses, buttonClasses.inActiveClasses),
                                }}
                                activeProps={{
                                    className: cn(buttonClasses.baseClasses, buttonClasses.activeClasses),
                                }}
                                activeOptions={{ includeSearch: tab.includeSearch }}
                            >
                                {t(tab.labelKey)}
                            </Link>
                        ))}
                    </div>
                </div>
            )}
            <Suspense
                fallback={
                    <div className="flex flex-1 justify-center py-16">
                        <Spinner />
                    </div>
                }
            >
                <Outlet />
            </Suspense>
        </section>
    );
};
